import { useState, useEffect } from 'react';

export function PracticeMode({ story, onScore, onCancel }) {
  const [phase, setPhase] = useState('ready'); // ready | recite | score
  const [time, setTime] = useState(0);
  const [revealed, setRevealed] = useState(new Set());
  const [actIdx, setActIdx] = useState(0);

  useEffect(() => {
    if (phase !== 'recite') return;
    const id = setInterval(() => setTime((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const actNames = story.actNames || ['Problem', 'Action', 'Result'];
  const acts = story.acts || [];
  const currentAct = acts[actIdx];

  function toggleReveal(key) {
    setRevealed((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }

  function nextAct() {
    if (actIdx < 2) { setActIdx(actIdx + 1); setRevealed(new Set()); }
    else setPhase('score');
  }

  const fmt = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  const overTime = time > 180;

  if (phase === 'ready') {
    return (
      <div className="p-6 max-w-2xl mx-auto text-center space-y-6">
        <h1 className="text-xl font-bold text-slate-900">{story.title}</h1>
        <p className="text-sm text-slate-500">Recite your story aloud. Use reveal buttons if you get stuck. Target: ~3 minutes.</p>
        <div className="flex justify-center gap-3">
          <button onClick={() => setPhase('recite')} className="rounded bg-primary-600 px-6 py-2 text-sm font-medium text-white hover:bg-primary-700">Start</button>
          <button onClick={onCancel} className="rounded px-4 py-2 text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
        </div>
      </div>
    );
  }

  if (phase === 'score') {
    return (
      <div className="p-6 max-w-md mx-auto text-center space-y-6">
        <h2 className="text-lg font-semibold text-slate-900">How did it go?</h2>
        <p className="text-sm text-slate-500">Time: {fmt(time)} {overTime && <span className="text-amber-600">(over 3 min)</span>}</p>
        <div className="flex justify-center gap-2">
          {[1, 2, 3, 4, 5].map((s) => (
            <button key={s} onClick={() => onScore(s)} className="w-10 h-10 rounded-full border-2 border-slate-300 text-sm font-bold hover:border-primary-500 hover:bg-primary-50">
              {s}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400">1 = struggled → 5 = nailed it</p>
      </div>
    );
  }

  // Recite phase
  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">{story.title}</h2>
        <span className={`text-sm font-mono ${overTime ? 'text-amber-600' : 'text-slate-500'}`}>{fmt(time)}</span>
      </div>

      <div className="flex gap-2 mb-4">
        {actNames.map((name, i) => (
          <button key={i} onClick={() => { setActIdx(i); setRevealed(new Set()); }} className={`px-3 py-1 text-xs rounded ${i === actIdx ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
            {name}
          </button>
        ))}
      </div>

      {currentAct && (
        <div className="space-y-3">
          {currentAct.bullets.map((bullet, bIdx) => {
            const bulletKey = `${actIdx}-${bIdx}`;
            const bulletRevealed = revealed.has(bulletKey);
            return (
              <div key={bIdx} className="rounded border border-slate-200 p-3">
                <button onClick={() => toggleReveal(bulletKey)} className="w-full text-left text-sm font-medium text-slate-700 flex items-center gap-2">
                  <span className="text-xs text-slate-400">{bIdx + 1}.</span>
                  {bulletRevealed ? bullet.text || <span className="italic text-slate-400">empty</span> : <span className="text-slate-400">tap to reveal</span>}
                </button>
                {bulletRevealed && bullet.details.some(d => d) && (
                  <ul className="mt-1 ml-5 space-y-0.5">
                    {bullet.details.filter(d => d).map((d, dIdx) => (
                      <li key={dIdx} className="text-xs text-slate-500">• {d}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-between pt-4">
        <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-700">End early</button>
        <button onClick={nextAct} className="rounded bg-primary-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-700">
          {actIdx < 2 ? `Next: ${actNames[actIdx + 1]}` : 'Finish & Score'}
        </button>
      </div>
    </div>
  );
}
